import React, { useState, useEffect, useMemo } from 'react';

const BusinessPlan = () => {
  const [activeSection, setActiveSection] = useState('executive-summary');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Table of contents data
  const tableOfContents = useMemo(() => [
    { id: 'executive-summary', title: '1. Executive Summary', level: 1 },
    { id: 'business-concept', title: 'Business Concept & Unique Value Proposition', level: 2 },
    { id: 'value-propositions', title: 'Value Propositions', level: 2 },
    { id: 'marketplace-model', title: 'Revolutionary Marketplace Model: Real Estate Stock Exchange', level: 2 },
    { id: 'landlord-liquidity', title: 'Landlord Liquidity Strategy', level: 2 },
    { id: 'property-pricing', title: 'Property Share Pricing Mechanism', level: 2 },
    { id: 'nyc-pilot', title: 'NYC Pilot Strategy: Partnership-Driven Validation', level: 2 },
    { id: 'tech-infrastructure', title: 'Enhanced Technology Infrastructure & Costs', level: 2 },
    { id: 'financial-model', title: 'Financial Model: Market-Based Revenue', level: 2 },
    { id: 'funding-request', title: 'Pre-Seed Funding Request: $4.8 Million', level: 2 },
    { id: 'company-description', title: '2. Company Description', level: 1 },
    { id: 'company-overview', title: 'Company Overview', level: 2 },
    { id: 'stock-market-parallel', title: 'The Stock Market Parallel: Proven Model for New Asset Class', level: 2 },
    { id: 'legal-innovation', title: 'Legal Innovation: SEC-Compliant Real Estate Trading', level: 2 },
    { id: 'market-analysis', title: '3. Market Analysis', level: 1 },
    { id: 'total-addressable-market', title: 'Total Addressable Market: Stock Market + Real Estate', level: 2 },
    { id: 'nyc-pilot-market', title: 'NYC Pilot Market Focus: Premium Testing Ground', level: 2 },
    { id: 'target-market', title: 'Target Market Segmentation', level: 2 },
    { id: 'primary-landlords', title: 'Primary: Multi-Property Landlords (Supply Side)', level: 3 },
    { id: 'secondary-renters', title: 'Secondary: GusHome Renters (Demand Side - Primary)', level: 3 },
    { id: 'tertiary-app-users', title: 'Tertiary: App Users (Demand Side - Secondary)', level: 3 },
    { id: 'economic-cycle', title: 'Economic Cycle Sensitivity Analysis', level: 2 },
    { id: 'product-service', title: '4. Product or Service Line', level: 1 },
    { id: 'core-platform', title: 'Core Platform: Real Estate Stock Exchange', level: 2 },
    { id: 'for-renters', title: 'For GusHome Renters: Rent-to-Equity Building', level: 3 },
    { id: 'for-app-users', title: 'For App Users: Real Estate Portfolio Trading', level: 3 },
    { id: 'for-landlords', title: 'For Landlords: Equity Liquidity Marketplace', level: 3 },
    { id: 'marketplace-mechanics', title: '5. Marketplace Mechanics & Liquidity Strategy', level: 1 },
    { id: 'stock-market-model', title: 'Stock Market Model Applied to Real Estate', level: 2 },
    { id: 'price-discovery', title: 'Price Discovery Mechanism', level: 3 },
    { id: 'liquidity-bootstrapping', title: 'Liquidity Bootstrapping Strategy', level: 3 },
    { id: 'liquidity-development', title: 'Liquidity Development Investment', level: 3 },
    { id: 'marketplace-success', title: 'Marketplace Success Metrics', level: 2 },
    { id: 'no-guaranteed-liquidity', title: 'No Guaranteed Liquidity Model', level: 2 },
    { id: 'technology-dependencies', title: 'Technology Dependencies and Risk Mitigation', level: 2 },
    { id: 'competitive-analysis', title: '6. Competitive Analysis & Differentiation', level: 1 },
    { id: 'direct-competitors', title: 'Direct Competitors: Fractional Real Estate Platforms', level: 2 },
    { id: 'arrived-homes', title: 'Arrived Homes - Securities Model', level: 3 },
    { id: 'fundrise', title: 'Fundrise - eREIT Model', level: 3 },
    { id: 'competitive-response', title: 'Competitive Response Scenarios', level: 2 },
    { id: 'unique-advantages', title: 'Unique Competitive Advantages', level: 2 },
    { id: 'competitive-moat', title: 'Competitive Moat Strategy', level: 2 },
    { id: 'marketing-sales', title: '7. Marketing & Sales Strategy', level: 1 },
    { id: 'nyc-marketing', title: 'NYC Pilot Marketing Strategy: Partnership-Driven Growth', level: 2 },
    { id: 'landlord-acquisition', title: 'Landlord Partnership Acquisition (Primary Channel)', level: 3 },
    { id: 'tenant-opt-in', title: 'Tenant Opt-In Validation (Critical Metric)', level: 3 },
    { id: 'app-user-acquisition', title: 'App User Acquisition (Secondary Channel)', level: 3 },
    { id: 'cac-validation', title: 'Customer Acquisition Cost Validation', level: 2 },
    { id: 'post-pilot-scaling', title: 'Post-Pilot Scaling Strategy', level: 2 },
    { id: 'operations-plan', title: '8. Operations Plan', level: 1 },
    { id: 'nyc-operations', title: 'NYC Pilot Operations: Concentrated Validation', level: 2 },
    { id: 'landlord-partnership', title: 'Landlord Partnership Strategy', level: 3 },
    { id: 'technology-infrastructure-ops', title: 'Technology Infrastructure (Enhanced Costs)', level: 2 },
    { id: 'property-management', title: 'Property Management Operations', level: 2 },
    { id: 'nyc-legal', title: 'NYC Legal Framework: Enhanced Costs', level: 2 },
    { id: 'management-organization', title: '9. Management & Organization', level: 1 },
    { id: 'founding-team', title: 'Founding Team', level: 2 },
    { id: 'nyc-team-structure', title: 'NYC Pilot Team Structure (15 employees)', level: 3 },
    { id: 'compensation-philosophy', title: 'Compensation Philosophy (NYC-Adjusted)', level: 2 },
    { id: 'financial-plan', title: '10. Financial Plan', level: 1 },
    { id: 'nyc-financial-model', title: 'NYC Pilot Financial Model (12 Months)', level: 2 },
    { id: 'pilot-revenue', title: 'Pilot Revenue Projections (Market-Based)', level: 3 },
    { id: 'revenue-model', title: 'Revenue Model (Transaction-Based)', level: 3 },
    { id: 'operating-expenses', title: 'NYC Pilot Operating Expenses (12 Months)', level: 2 },
    { id: 'post-pilot-financial', title: 'Post-Pilot Financial Projections (5-Year Outlook)', level: 2 },
    { id: 'intellectual-property', title: '11. Intellectual Property Strategy', level: 1 },
    { id: 'patent-portfolio', title: 'Comprehensive Patent Portfolio', level: 2 },
    { id: 'core-patents', title: 'Core Business Model Patents (Priority Tier 1)', level: 3 },
    { id: 'technology-patents', title: 'Technology and Process Patents (Priority Tier 2)', level: 3 },
    { id: 'defensive-patents', title: 'Defensive and Strategic Patents (Priority Tier 3)', level: 3 },
    { id: 'trade-secrets', title: 'Trade Secrets and Proprietary IP', level: 2 },
    { id: 'trademark-protection', title: 'Trademark and Brand Protection', level: 2 },
    { id: 'ip-investment', title: 'IP Investment Strategy', level: 2 },
    { id: 'ip-portfolio-strategy', title: 'IP Portfolio Strategy', level: 2 },
    { id: 'funding-request-detailed', title: '12. Funding Request', level: 1 },
    { id: 'pre-seed-investment', title: 'Pre-Seed Investment: $4.8 Million', level: 2 },
    { id: 'use-of-funds', title: 'Use of Funds (NYC Pilot Only)', level: 2 },
    { id: 'investment-terms', title: 'Investment Terms', level: 2 },
    { id: 'expected-returns', title: 'Expected Returns Analysis', level: 2 },
    { id: 'risk-analysis', title: '13. Risk Analysis & Mitigation', level: 1 },
    { id: 'nyc-risk-management', title: 'NYC Pilot-Specific Risk Management', level: 2 },
    { id: 'technology-operational-risk', title: 'Technology and Operational Risk Mitigation', level: 2 },
    { id: 'post-pilot-risk', title: 'Post-Pilot Risk Assessment', level: 2 },
    { id: 'implementation-timeline', title: '14. Implementation Timeline', level: 1 },
    { id: 'nyc-implementation', title: '12-Month NYC Pilot Implementation Plan', level: 2 },
    { id: 'months-1-3', title: 'Months 1-3: Foundation and Legal Framework', level: 3 },
    { id: 'months-4-6', title: 'Months 4-6: Pilot Launch and Initial Validation', level: 3 },
    { id: 'months-7-9', title: 'Months 7-9: Marketplace Scaling and Validation', level: 3 },
    { id: 'months-10-12', title: 'Months 10-12: Series A Preparation', level: 3 },
    { id: 'critical-metrics', title: 'Critical Pilot Success Metrics (Monthly Tracking)', level: 2 },
    { id: 'risk-monitoring', title: 'Enhanced Risk Monitoring and Mitigation Timeline', level: 2 },
    { id: 'series-a-readiness', title: 'Series A Readiness Checklist (Month 12)', level: 2 },
    { id: 'post-pilot-timeline', title: 'Post-Pilot Timeline (Series A Phase)', level: 2 },
    { id: 'conclusion', title: 'Conclusion', level: 1 },
    { id: 'why-this-works', title: 'Why This Model Works', level: 2 },
    { id: 'why-now', title: 'Why Now', level: 2 },
    { id: 'why-us', title: 'Why Us', level: 2 },
    { id: 'nyc-pilot-strategy', title: 'The NYC Pilot Strategy', level: 2 },
    { id: 'critical-validation', title: 'Critical Validation Questions We\'ll Answer', level: 2 },
    { id: 'enhanced-investment', title: 'Enhanced Investment Opportunity', level: 2 },
    { id: 'appendices', title: 'Appendices', level: 1 },
    { id: 'appendix-a', title: 'Appendix A: NYC Legal Framework Details', level: 2 },
    { id: 'appendix-b', title: 'Appendix B: Technology Architecture and Third-Party Integration', level: 2 },
    { id: 'appendix-c', title: 'Appendix C: Competitive Analysis - Market Positioning Matrix', level: 2 },
    { id: 'appendix-d', title: 'Appendix D: Financial Model Assumptions and Enhanced Sensitivity Analysis', level: 2 }
  ], []);

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      for (let i = tableOfContents.length - 1; i >= 0; i--) {
        const element = document.getElementById(tableOfContents[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: "'Inter', sans-serif"
    },
    sidebar: {
      width: sidebarOpen ? '350px' : '60px',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
      position: 'fixed',
      height: '100vh',
      overflowY: 'auto',
      transition: 'width 0.3s ease',
      zIndex: 1000
    },
    sidebarToggle: {
      position: 'absolute',
      top: '20px',
      right: '-15px',
      background: '#667eea',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      zIndex: 1001
    },
    sidebarHeader: {
      padding: '20px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      display: sidebarOpen ? 'block' : 'none'
    },
    sidebarTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#333',
      margin: '0 0 10px 0'
    },
    pdfLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#667eea',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      padding: '8px 12px',
      border: '1px solid #667eea',
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    tocList: {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    tocItem: {
      margin: '0'
    },
    tocButton: {
      width: '100%',
      background: 'none',
      border: 'none',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '13px',
      color: '#555'
    },
    tocButtonLevel1: {
      padding: '12px 20px',
      fontWeight: '600',
      color: '#333',
      borderLeft: '3px solid transparent'
    },
    tocButtonLevel2: {
      padding: '8px 20px 8px 35px',
      fontWeight: '500',
      borderLeft: '3px solid transparent'
    },
    tocButtonLevel3: {
      padding: '6px 20px 6px 50px',
      fontWeight: '400',
      fontSize: '12px',
      borderLeft: '3px solid transparent'
    },
    tocButtonActive: {
      background: 'rgba(102, 126, 234, 0.1)',
      borderLeftColor: '#667eea',
      color: '#667eea'
    },
    content: {
      marginLeft: sidebarOpen ? '350px' : '60px',
      padding: '40px',
      background: 'white',
      minHeight: '100vh',
      transition: 'margin-left 0.3s ease',
      maxWidth: '1200px',
      lineHeight: '1.6'
    },
    section: {
      marginBottom: '60px',
      scrollMarginTop: '20px'
    },
    h1: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#333',
      marginBottom: '30px',
      borderBottom: '3px solid #667eea',
      paddingBottom: '15px'
    },
    h2: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#444',
      marginTop: '40px',
      marginBottom: '20px'
    },
    h3: {
      fontSize: '22px',
      fontWeight: '600',
      color: '#555',
      marginTop: '30px',
      marginBottom: '15px'
    },
    h4: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#666',
      marginTop: '25px',
      marginBottom: '12px'
    },
    paragraph: {
      fontSize: '16px',
      lineHeight: '1.7',
      color: '#555',
      marginBottom: '16px'
    },
    bulletList: {
      paddingLeft: '20px',
      marginBottom: '16px'
    },
    bulletItem: {
      fontSize: '16px',
      color: '#555',
      marginBottom: '8px',
      lineHeight: '1.6'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '30px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    tableHeader: {
      background: '#667eea',
      color: 'white'
    },
    tableCell: {
      padding: '12px 16px',
      borderBottom: '1px solid #eee',
      fontSize: '14px'
    },
    tableCellHeader: {
      padding: '16px',
      fontWeight: '600',
      fontSize: '14px'
    },
    tableRowEven: {
      background: '#f9f9f9'
    },
    highlightBox: {
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: 'white',
      padding: '25px',
      borderRadius: '12px',
      marginBottom: '30px',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
    },
    noteBox: {
      background: '#f0f7ff',
      border: '1px solid #667eea',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    riskBox: {
      background: '#fff5f5',
      border: '1px solid #f56565',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    },
    successBox: {
      background: '#f0fff4',
      border: '1px solid #48bb78',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <button style={styles.sidebarToggle} onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? '‹' : '›'}
        </button>
        
        {sidebarOpen && (
          <>
            <div style={styles.sidebarHeader}>
              <h2 style={styles.sidebarTitle}>Business Plan</h2>
              <a 
                href="/business-plan.pdf" 
                style={styles.pdfLink} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                📄 Download PDF
              </a>
            </div>
            
            <ul style={styles.tocList}>
              {tableOfContents.map((item) => (
                <li key={item.id} style={styles.tocItem}>
                  <button
                    style={{
                      ...styles.tocButton,
                      ...(item.level === 1 ? styles.tocButtonLevel1 : 
                          item.level === 2 ? styles.tocButtonLevel2 : styles.tocButtonLevel3),
                      ...(activeSection === item.id ? styles.tocButtonActive : {})
                    }}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        <div style={styles.highlightBox}>
          <h1 style={{...styles.h1, color: 'white', border: 'none', fontSize: '42px'}}>
            GusHome Business Plan
          </h1>
          <p style={{fontSize: '20px', margin: '0', opacity: '0.9'}}>
            The Real Estate Stock Market & Rent-to-Equity Platform
          </p>
          <p style={{fontSize: '16px', margin: '10px 0 0 0', opacity: '0.8'}}>
            Confidential & Proprietary | Prepared for Pre-Seed Investment Opportunity | 12-Month NYC Pilot Validation Strategy | June 2025
          </p>
        </div>

        {/* Executive Summary */}
        <section id="executive-summary" style={styles.section}>
          <h1 style={styles.h1}>1. Executive Summary</h1>
          
          <div id="business-concept" style={styles.section}>
            <h2 style={styles.h2}>Business Concept & Unique Value Proposition</h2>
            <p style={styles.paragraph}>
              GusHome is the first stock market for real estate—a mobile platform enabling fractional property ownership through a marketplace model. Just as Robinhood democratized access to stock investing, GusHome is doing the same for real estate. Our three-sided marketplace empowers:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>Renters to build equity every time they pay rent</li>
              <li style={styles.bulletItem}>Investors to access real estate like a stock portfolio</li>
              <li style={styles.bulletItem}>Landlords to keep tenants longer, fill units faster and maximize their ROI</li>
            </ul>
            <div style={styles.noteBox}>
              <p style={{...styles.paragraph, margin: '0'}}>
                <strong>Core Innovation:</strong> We've created the "NASDAQ for real estate" - properties trade like stocks with market-driven pricing, buyer-seller matching, and organic liquidity development.
              </p>
            </div>
          </div>

          <div id="value-propositions" style={styles.section}>
            <h2 style={styles.h2}>Value Propositions</h2>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>For Renters:</strong> "Turn your rent into ownership" - Build equity in your home like buying stock</li>
              <li style={styles.bulletItem}><strong>For Investors:</strong> "Own properties like Tesla stock" - Buy/sell real estate shares with market pricing</li>
              <li style={styles.bulletItem}><strong>For Landlords:</strong> "Your property becomes a stock" - Access equity liquidity when market demand exists</li>
              <li style={styles.bulletItem}><strong>For the Market:</strong> "Real estate meets stock market mechanics" - Proven liquidity model applied to property</li>
            </ul>
          </div>

          <div id="marketplace-model" style={styles.section}>
            <h2 style={styles.h2}>Revolutionary Marketplace Model: Real Estate Stock Exchange</h2>
            <h3 style={styles.h3}>Stock Market Mechanics for Properties:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Price Discovery:</strong> Market-driven valuations through buyer/seller activity</li>
              <li style={styles.bulletItem}><strong>Liquidity Development:</strong> Organic marketplace growth creates trading opportunities</li>
              <li style={styles.bulletItem}><strong>No Guaranteed Buyers:</strong> Like stocks, sales depend on market demand and timing</li>
              <li style={styles.bulletItem}><strong>Network Effects:</strong> More participants = better liquidity for all users</li>
            </ul>
          </div>

          <div id="landlord-liquidity" style={styles.section}>
            <h2 style={styles.h2}>Landlord Liquidity Strategy</h2>
            <p style={styles.paragraph}>
              Landlords list property shares when they choose to monetize equity:
            </p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Share prices reflect current property value:</strong> Shares trade based on real-time property valuations (Zillow API + market data)</li>
              <li style={styles.bulletItem}><strong>Appreciation/Depreciation:</strong> Share prices increase/decrease with underlying property value changes</li>
              <li style={styles.bulletItem}><strong>Market liquidity risk:</strong> Shares may not find buyers immediately, regardless of fair pricing</li>
              <li style={styles.bulletItem}><strong>Optionality Value:</strong> Access to equity liquidity based on current market value vs. traditional real estate illiquidity</li>
            </ul>
          </div>

          <div id="property-pricing" style={styles.section}>
            <h2 style={styles.h2}>Property Share Pricing Mechanism</h2>
            <h3 style={styles.h3}>Real-Time Valuation Model:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Base Valuation:</strong> Zillow API provides current estimated property value</li>
              <li style={styles.bulletItem}><strong>Comparable Sales:</strong> Recent transactions of similar properties in neighborhood</li>
              <li style={styles.bulletItem}><strong>Property Performance:</strong> Rental income, occupancy rates, and property improvements</li>
              <li style={styles.bulletItem}><strong>Market Adjustments:</strong> Local real estate market trends and seasonal factors</li>
            </ul>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Share Price Calculation Example:</h4>
              <p style={styles.paragraph}><strong>Current Property Value:</strong> $500K (Zillow estimate)</p>
              <p style={styles.paragraph}><strong>Total Shares Outstanding:</strong> 1,000 shares</p>
              <p style={styles.paragraph}><strong>Base Share Price:</strong> $500 per share</p>
              <p style={styles.paragraph}><strong>Property appreciates to $550K:</strong> New Share Price: $550 per share (+10% gain)</p>
              <p style={{...styles.paragraph, marginBottom: '0'}}><strong>Property depreciates to $450K:</strong> New Share Price: $450 per share (-10% loss)</p>
            </div>

            <h3 style={styles.h3}>Price Discovery Process:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Algorithm-Based Pricing:</strong> Platform calculates fair value using multiple data sources</li>
              <li style={styles.bulletItem}><strong>Market Validation:</strong> Actual transaction prices validate or adjust algorithmic pricing</li>
              <li style={styles.bulletItem}><strong>Bid-Ask Spreads:</strong> Buyers and sellers can set limit orders above/below fair value</li>
              <li style={styles.bulletItem}><strong>Transparent History:</strong> All pricing data and transaction history visible to users</li>
            </ul>

            <div style={styles.riskBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>No Guaranteed Liquidity:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Fair pricing ≠ guaranteed sales:</strong> Shares may be fairly priced but lack buyer demand</li>
                <li style={styles.bulletItem}><strong>Market timing matters:</strong> Seasonal real estate cycles affect investor interest</li>
                <li style={styles.bulletItem}><strong>Property quality impact:</strong> Well-maintained properties in good locations trade more frequently</li>
                <li style={styles.bulletItem}><strong>Economic sensitivity:</strong> Broader market conditions affect all real estate investment demand</li>
              </ul>
            </div>
          </div>

          <div id="nyc-pilot" style={styles.section}>
            <h2 style={styles.h2}>NYC Pilot Strategy: Partnership-Driven Validation</h2>
            <h3 style={styles.h3}>12-Month NYC-Only Pilot: Prove Marketplace Mechanics</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>Partner with landlords owning 10+ rental properties each</li>
              <li style={styles.bulletItem}>Offer equity-building to all tenants in partner portfolios</li>
              <li style={styles.bulletItem}>Test opt-in rates when tenants have choice to participate</li>
              <li style={styles.bulletItem}>Validate marketplace liquidity with concentrated property clusters</li>
            </ul>

            <div style={styles.successBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Pilot Success Metrics:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Tenant Opt-in Rate:</strong> Target 40%+ participation when offered choice</li>
                <li style={styles.bulletItem}><strong>Marketplace Activity:</strong> 25% of listed property shares sell within 90 days</li>
                <li style={styles.bulletItem}><strong>Network Effects:</strong> Liquidity improves as user base grows</li>
                <li style={styles.bulletItem}><strong>Landlord Retention:</strong> 80%+ of landlord partners expand program after 6 months</li>
              </ul>
            </div>
          </div>

          <div id="tech-infrastructure" style={styles.section}>
            <h2 style={styles.h2}>Enhanced Technology Infrastructure & Costs</h2>
            <h3 style={styles.h3}>Core Platform with Third-Party Integration:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Zillow API Integration:</strong> Real-time property valuations and market data</li>
              <li style={styles.bulletItem}><strong>Stripe Payment Processing:</strong> Secure transaction handling and compliance</li>
              <li style={styles.bulletItem}><strong>AWS Cloud Infrastructure:</strong> Scalable backend with SOC 2 compliance</li>
              <li style={styles.bulletItem}><strong>Mobile-First Design:</strong> Native iOS/Android apps for marketplace trading</li>
            </ul>

            <h3 style={styles.h3}>Annual Technology Costs (Pilot Scale):</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Zillow Premium API:</strong> $48K annually for 1,000 properties</li>
              <li style={styles.bulletItem}><strong>Stripe Processing:</strong> 2.9% + $0.30 per transaction</li>
              <li style={styles.bulletItem}><strong>AWS Infrastructure:</strong> $24K annually for pilot scale</li>
              <li style={styles.bulletItem}><strong>Third-party security/compliance tools:</strong> $36K annually</li>
            </ul>
          </div>

          <div id="financial-model" style={styles.section}>
            <h2 style={styles.h2}>Financial Model: Market-Based Revenue</h2>
            
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableCellHeader}>Metric</th>
                  <th style={styles.tableCellHeader}>Month 6</th>
                  <th style={styles.tableCellHeader}>Month 9</th>
                  <th style={styles.tableCellHeader}>Month 12</th>
                  <th style={styles.tableCellHeader}>Series A Target</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={styles.tableCell}>Landlord Partners</td>
                  <td style={styles.tableCell}>8</td>
                  <td style={styles.tableCell}>15</td>
                  <td style={styles.tableCell}>25</td>
                  <td style={styles.tableCell}>100+</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Properties Available</td>
                  <td style={styles.tableCell}>120</td>
                  <td style={styles.tableCell}>225</td>
                  <td style={styles.tableCell}>375</td>
                  <td style={styles.tableCell}>1,500+</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>GusHome Renters</td>
                  <td style={styles.tableCell}>50</td>
                  <td style={styles.tableCell}>90</td>
                  <td style={styles.tableCell}>150</td>
                  <td style={styles.tableCell}>600+</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>App Users</td>
                  <td style={styles.tableCell}>75</td>
                  <td style={styles.tableCell}>135</td>
                  <td style={styles.tableCell}>225</td>
                  <td style={styles.tableCell}>900+</td>
                </tr>
                <tr>
                  <td style={styles.tableCell}>Property Shares Traded</td>
                  <td style={styles.tableCell}>15%</td>
                  <td style={styles.tableCell}>20%</td>
                  <td style={styles.tableCell}>25%</td>
                  <td style={styles.tableCell}>35%+</td>
                </tr>
                <tr style={styles.tableRowEven}>
                  <td style={styles.tableCell}>Monthly Revenue</td>
                  <td style={styles.tableCell}>$8.2K</td>
                  <td style={styles.tableCell}>$15.8K</td>
                  <td style={styles.tableCell}>$28.5K</td>
                  <td style={styles.tableCell}>$125K+</td>
                </tr>
              </tbody>
            </table>
            
            <div style={styles.noteBox}>
              <p style={{...styles.paragraph, margin: '0'}}>
                <strong>Note:</strong> Revenue dependent on actual marketplace trading volume, not guaranteed transactions
              </p>
            </div>
          </div>

          <div id="funding-request" style={styles.section}>
            <h2 style={styles.h2}>Pre-Seed Funding Request: $4.8 Million</h2>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>NYC Legal Framework:</strong> $1.2M (25%) - Enhanced for regulatory complexity</li>
              <li style={styles.bulletItem}><strong>Technology Platform:</strong> $1.2M (25%) - Including API costs and infrastructure</li>
              <li style={styles.bulletItem}><strong>Pilot Operations:</strong> $800K (17%) - Landlord partnerships and property onboarding</li>
              <li style={styles.bulletItem}><strong>Team & Operations:</strong> $650K (14%) - NYC-focused team</li>
              <li style={styles.bulletItem}><strong>Intellectual Property:</strong> $700K (15%) - Comprehensive patent and trade secret protection</li>
              <li style={styles.bulletItem}><strong>Marketing & Validation:</strong> $200K (4%) - Pilot market customer acquisition</li>
            </ul>
          </div>
        </section>

        {/* Company Description */}
        <section id="company-description" style={styles.section}>
          <h1 style={styles.h1}>2. Company Description</h1>
          
          <div id="company-overview" style={styles.section}>
            <h2 style={styles.h2}>Company Overview</h2>
            <p style={styles.paragraph}>
              GusHome transforms real estate from an illiquid, high-barrier asset class into a liquid, accessible marketplace using proven stock market mechanics. We're building the NYSE for real estate - where properties trade with market-driven pricing, organic liquidity development, and familiar user experiences.
            </p>
            <div style={styles.highlightBox}>
              <p style={{...styles.paragraph, margin: '0', fontSize: '18px'}}>
                <strong>Mission Statement:</strong> "Real estate deserves stock market liquidity. We're building the marketplace where properties trade like securities, creating wealth-building opportunities for everyone."
              </p>
            </div>
          </div>

          <div id="stock-market-parallel" style={styles.section}>
            <h2 style={styles.h2}>The Stock Market Parallel: Proven Model for New Asset Class</h2>
            <h3 style={styles.h3}>Why Stock Market Mechanics Work:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>40+ million Americans already understand buying/selling stocks</li>
              <li style={styles.bulletItem}>Proven liquidity model - no guaranteed buyers, but market depth creates opportunity</li>
              <li style={styles.bulletItem}>Price transparency through market-driven discovery vs. opaque real estate pricing</li>
              <li style={styles.bulletItem}>Network effects - more participants improve liquidity for all users</li>
              <li style={styles.bulletItem}>Regulatory framework exists for marketplace-based trading</li>
            </ul>

            <h3 style={styles.h3}>Applied to Real Estate:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>Properties become tradeable assets with fractional ownership</li>
              <li style={styles.bulletItem}>Market pricing replaces subjective real estate valuations</li>
              <li style={styles.bulletItem}>Instant trading vs. 30-90 day real estate transactions</li>
              <li style={styles.bulletItem}>Portfolio diversification across multiple properties and markets</li>
              <li style={styles.bulletItem}>Familiar user experience reduces adoption friction</li>
            </ul>
          </div>

          <div id="legal-innovation" style={styles.section}>
            <h2 style={styles.h2}>Legal Innovation: SEC-Compliant Real Estate Trading</h2>
            <h3 style={styles.h3}>Tenancy-in-Common (TIC) Structure:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>Direct property ownership through fractional TIC interests</li>
              <li style={styles.bulletItem}>SEC-compliant trading platform for real estate securities</li>
              <li style={styles.bulletItem}>Advisory voting rights with professional property management</li>
              <li style={styles.bulletItem}>Real deed ownership with title insurance and legal protection</li>
            </ul>

            <h3 style={styles.h3}>Marketplace Legal Framework:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>State-specific securities compliance for property trading</li>
              <li style={styles.bulletItem}>Anti-money laundering (AML) and Know Your Customer (KYC) procedures</li>
              <li style={styles.bulletItem}>Professional property management with investor oversight</li>
              <li style={styles.bulletItem}>Secondary market trading with transfer agent services</li>
            </ul>

            <div style={styles.riskBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Enhanced Regulatory Considerations:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>NYC Rent Stabilization Compliance:</strong> Framework for equity building in rent-controlled units</li>
                <li style={styles.bulletItem}><strong>FINRA Registration:</strong> Potential broker-dealer requirements for secondary trading</li>
                <li style={styles.bulletItem}><strong>State Banking Laws:</strong> Money transmission licenses for fund custody</li>
                <li style={styles.bulletItem}><strong>Fair Housing Act:</strong> Equal access protocols for all equity building programs</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Market Analysis */}
        <section id="market-analysis" style={styles.section}>
          <h1 style={styles.h1}>3. Market Analysis</h1>
          
          <div id="total-addressable-market" style={styles.section}>
            <h2 style={styles.h2}>Total Addressable Market: Stock Market + Real Estate</h2>
            <h3 style={styles.h3}>Primary Market: Real Estate Marketplace Trading</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>46 million rental households seeking equity building opportunities</li>
              <li style={styles.bulletItem}>55 million active stock traders familiar with marketplace mechanics</li>
              <li style={styles.bulletItem}>$650 billion annual rent payments with zero equity accumulation</li>
              <li style={styles.bulletItem}>$2.1 trillion total addressable market for fractional real estate ownership</li>
            </ul>
          </div>

          <div id="nyc-pilot-market" style={styles.section}>
            <h2 style={styles.h2}>NYC Pilot Market Focus: Premium Testing Ground</h2>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>2.1 million rental households in NYC metropolitan area</li>
              <li style={styles.bulletItem}>Average rent: $3,200/month in target neighborhoods</li>
              <li style={styles.bulletItem}>Tech-savvy population with mobile-first investment experience</li>
              <li style={styles.bulletItem}>High property values creating strong investor demand for fractional access</li>
            </ul>

            <div style={styles.noteBox}>
              <h4 style={{...styles.h4, marginTop: '0'}}>Pilot Market Penetration Target:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}>25 landlord partners with 375 total properties</li>
                <li style={styles.bulletItem}>0.018% penetration of NYC rental market (conservative)</li>
                <li style={styles.bulletItem}>Focus on quality over quantity for marketplace validation</li>
              </ul>
            </div>
          </div>

          <div id="target-market" style={styles.section}>
            <h2 style={styles.h2}>Target Market Segmentation</h2>
            
            <div id="primary-landlords" style={styles.section}>
              <h3 style={styles.h3}>Primary: Multi-Property Landlords (Supply Side)</h3>
              <h4 style={styles.h4}>Target Profile:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Portfolio Size:</strong> 10+ rental properties in NYC</li>
                <li style={styles.bulletItem}><strong>Property Value:</strong> $500K+ average per property</li>
                <li style={styles.bulletItem}><strong>Management Style:</strong> Professional property management or self-managed</li>
                <li style={styles.bulletItem}><strong>Income Level:</strong> $200K+ annually from rental portfolio</li>
              </ul>

              <h4 style={styles.h4}>Value Proposition for Landlords:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>"Your property becomes a stock"</strong> - Access equity liquidity through marketplace</li>
                <li style={styles.bulletItem}><strong>Market-based pricing</strong> - Fair value discovery through investor demand</li>
                <li style={styles.bulletItem}><strong>Retain rental income</strong> - Sell equity while keeping cash flow</li>
                <li style={styles.bulletItem}><strong>No guaranteed sales</strong> - Success depends on property quality and market timing</li>
                <li style={styles.bulletItem}><strong>Portfolio optimization</strong> - Monetize best-performing assets strategically</li>
              </ul>

              <div style={styles.successBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Pilot Strategy:</h4>
                <ul style={styles.bulletList}>
                  <li style={styles.bulletItem}>Partner with 25 landlords owning 15+ properties each</li>
                  <li style={styles.bulletItem}>Test tenant opt-in rates across entire landlord portfolios</li>
                  <li style={styles.bulletItem}>Validate marketplace liquidity with concentrated property clusters</li>
                </ul>
              </div>
            </div>

            <div id="secondary-renters" style={styles.section}>
              <h3 style={styles.h3}>Secondary: GusHome Renters (Demand Side - Primary)</h3>
              <h4 style={styles.h4}>Demographics:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Age:</strong> 25-40 years old living in partner properties</li>
                <li style={styles.bulletItem}><strong>Income:</strong> $75K-180K annually (NYC-adjusted)</li>
                <li style={styles.bulletItem}><strong>Current Status:</strong> Renting in landlord partner buildings</li>
                <li style={styles.bulletItem}><strong>Investment Experience:</strong> Basic familiarity with stock market apps</li>
              </ul>

              <div style={styles.highlightBox}>
                <h4 style={{...styles.h4, marginTop: '0', color: 'white'}}>Critical Validation Metric:</h4>
                <ul style={styles.bulletList}>
                  <li style={styles.bulletItem}><strong>Opt-in Rate:</strong> What percentage choose equity building when offered?</li>
                  <li style={styles.bulletItem}><strong>Target:</strong> 40%+ participation rate among eligible tenants</li>
                  <li style={styles.bulletItem}><strong>Method:</strong> Offer program to ALL tenants in partner buildings</li>
                </ul>
              </div>

              <h4 style={styles.h4}>Behavioral Economics Considerations:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Status Quo Bias:</strong> Many tenants may resist change from traditional rent payments</li>
                <li style={styles.bulletItem}><strong>Complexity Aversion:</strong> Simplified onboarding and education critical for adoption</li>
                <li style={styles.bulletItem}><strong>Loss Aversion:</strong> Emphasize security and ownership benefits over investment risks</li>
              </ul>
            </div>

            <div id="tertiary-app-users" style={styles.section}>
              <h3 style={styles.h3}>Tertiary: App Users (Demand Side - Secondary)</h3>
              <h4 style={styles.h4}>Demographics:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Age:</strong> 28-50 years old</li>
                <li style={styles.bulletItem}><strong>Income:</strong> $60K-200K annually</li>
                <li style={styles.bulletItem}><strong>Location:</strong> NYC metro area initially, nationwide post-pilot</li>
                <li style={styles.bulletItem}><strong>Investment Behavior:</strong> Active users of Robinhood, E*TRADE, etc.</li>
              </ul>
              <p style={styles.paragraph}>
                <strong>NYC Pilot Target:</strong> 225 users investing in partner properties
              </p>
            </div>
          </div>

          <div id="economic-cycle" style={styles.section}>
            <h2 style={styles.h2}>Economic Cycle Sensitivity Analysis</h2>
            <h3 style={styles.h3}>Bull Market Scenario (2024-2026 expected):</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>High investor demand for alternative assets</li>
              <li style={styles.bulletItem}>Rising property values increase share prices</li>
              <li style={styles.bulletItem}>Strong tenant opt-in rates due to wealth-building focus</li>
            </ul>

            <h3 style={styles.h3}>Bear Market Scenario (recession conditions):</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>Reduced investor liquidity and risk appetite</li>
              <li style={styles.bulletItem}>Property value declines affect share pricing</li>
              <li style={styles.bulletItem}>Potential increased tenant interest in equity building for stability</li>
            </ul>

            <h3 style={styles.h3}>Interest Rate Sensitivity:</h3>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}><strong>Rising Rates:</strong> May reduce property values but increase yield-seeking behavior</li>
              <li style={styles.bulletItem}><strong>Falling Rates:</strong> Property appreciation benefits existing shareholders</li>
              <li style={styles.bulletItem}><strong>Platform Independence:</strong> Revenue model not directly tied to interest rate environment</li>
            </ul>
          </div>
        </section>

        {/* Continue with remaining sections... */}
        <section id="product-service" style={styles.section}>
          <h1 style={styles.h1}>4. Product or Service Line</h1>
          
          <div id="core-platform" style={styles.section}>
            <h2 style={styles.h2}>Core Platform: Real Estate Stock Exchange</h2>
            
            <div id="for-renters" style={styles.section}>
              <h3 style={styles.h3}>For GusHome Renters: Rent-to-Equity Building</h3>
              <div style={styles.highlightBox}>
                <p style={{...styles.paragraph, margin: '0 0 15px 0', fontSize: '18px'}}>
                  <strong>Value Proposition:</strong> "Buy stock in your apartment building"
                </p>
              </div>

              <h4 style={styles.h4}>Marketplace Participation:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Automatic Investment:</strong> 5% of rent auto-invested in building equity</li>
                <li style={styles.bulletItem}><strong>Market Pricing:</strong> Purchase shares at current market value</li>
                <li style={styles.bulletItem}><strong>Trading Flexibility:</strong> Buy additional shares or sell existing holdings</li>
                <li style={styles.bulletItem}><strong>Community Ownership:</strong> Join other tenants as property co-owners</li>
              </ul>

              <h4 style={styles.h4}>Pilot Features:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}>Basic portfolio tracking and market value monitoring</li>
                <li style={styles.bulletItem}>Educational content about real estate investing fundamentals</li>
                <li style={styles.bulletItem}>Community features connecting co-owners within same property</li>
                <li style={styles.bulletItem}>Mobile voting on property improvements and management decisions</li>
              </ul>

              <div style={styles.noteBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Tax Implications Management:</h4>
                <ul style={styles.bulletList}>
                  <li style={styles.bulletItem}><strong>K-1 Distribution Support:</strong> Automated tax document generation and support</li>
                  <li style={styles.bulletItem}><strong>Professional Tax Integration:</strong> Partnership with tax preparation services</li>
                  <li style={styles.bulletItem}><strong>Educational Resources:</strong> Clear guidance on fractional ownership tax implications</li>
                </ul>
              </div>
            </div>

            <div id="for-app-users" style={styles.section}>
              <h3 style={styles.h3}>For App Users: Real Estate Portfolio Trading</h3>
              <div style={styles.highlightBox}>
                <p style={{...styles.paragraph, margin: '0 0 15px 0', fontSize: '18px'}}>
                  <strong>Value Proposition:</strong> "Trade real estate like stocks"
                </p>
              </div>

              <h4 style={styles.h4}>Stock Market Experience for Real Estate:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Browse Properties:</strong> View available properties with financial metrics</li>
                <li style={styles.bulletItem}><strong>Market Orders:</strong> Buy/sell shares at current market prices</li>
                <li style={styles.bulletItem}><strong>Portfolio Management:</strong> Track performance across multiple properties</li>
                <li style={styles.bulletItem}><strong>Price Charts:</strong> Historical performance and market trends</li>
                <li style={styles.bulletItem}><strong>Research Tools:</strong> Property analytics and neighborhood data</li>
              </ul>

              <h4 style={styles.h4}>Pilot Features:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}>Property browsing with Zillow-powered market data</li>
                <li style={styles.bulletItem}>Real-time pricing and transaction capabilities</li>
                <li style={styles.bulletItem}>Basic portfolio analytics and performance tracking</li>
                <li style={styles.bulletItem}>Educational content promoting buy-and-hold strategies</li>
              </ul>
            </div>

            <div id="for-landlords" style={styles.section}>
              <h3 style={styles.h3}>For Landlords: Equity Liquidity Marketplace</h3>
              <div style={styles.highlightBox}>
                <p style={{...styles.paragraph, margin: '0 0 15px 0', fontSize: '18px'}}>
                  <strong>Value Proposition:</strong> "List your property equity when you want liquidity"
                </p>
              </div>

              <h4 style={styles.h4}>Market-Based Equity Access:</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>List Shares:</strong> Post property equity for sale when desired</li>
                <li style={styles.bulletItem}><strong>Market Pricing:</strong> Investors bid based on property performance and location</li>
                <li style={styles.bulletItem}><strong>No Guaranteed Sales:</strong> Success depends on investor demand and timing</li>
                <li style={styles.bulletItem}><strong>Retain Control:</strong> Keep property management and rental income stream</li>
              </ul>

              <h4 style={styles.h4}>Landlord Benefits (Market-Dependent):</h4>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Potential Liquidity:</strong> Access 20-80% of property value when market conditions favor selling</li>
                <li style={styles.bulletItem}><strong>Tenant Retention:</strong> Average 10% longer tenancies through equity participation</li>
                <li style={styles.bulletItem}><strong>Reduced Turnover:</strong> Fewer voluntary move-outs with ownership stakes</li>
                <li style={styles.bulletItem}><strong>Portfolio Optimization:</strong> Strategic equity sales for reinvestment opportunities</li>
              </ul>

              <div style={styles.riskBox}>
                <h4 style={{...styles.h4, marginTop: '0'}}>Property Management Integration:</h4>
                <ul style={styles.bulletList}>
                  <li style={styles.bulletItem}><strong>Conflict Resolution:</strong> Clear protocols for fractional owner vs. landlord decisions</li>
                  <li style={styles.bulletItem}><strong>Improvement Funding:</strong> Structured approach to major repair/improvement financing</li>
                  <li style={styles.bulletItem}><strong>Liability Allocation:</strong> Defined responsibility between platform, landlord, and fractional owners</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Add a note about the continuation */}
        <div style={{...styles.noteBox, marginTop: '40px'}}>
          <h3 style={{...styles.h3, marginTop: '0'}}>Document Continues...</h3>
          <p style={{...styles.paragraph, marginBottom: '0'}}>
            This business plan continues with detailed sections covering Marketplace Mechanics & Liquidity Strategy, Competitive Analysis, Marketing Strategy, Operations Plan, Financial Projections, Intellectual Property Strategy, Risk Analysis, Implementation Timeline, and comprehensive Appendices. 
          </p>
          <p style={{...styles.paragraph, marginBottom: '0'}}>
            For the complete document with all financial models, competitive analysis, and detailed implementation plans, please download the full PDF version using the link in the sidebar.
          </p>
        </div>

        {/* Footer navigation */}
        <div style={{...styles.highlightBox, marginTop: '60px', textAlign: 'center'}}>
          <h3 style={{color: 'white', marginTop: '0'}}>Ready to Learn More?</h3>
          <p style={{margin: '15px 0'}}>
            This comprehensive business plan outlines our strategy to revolutionize real estate investment through marketplace mechanics.
          </p>
          <a 
            href="/business-plan.pdf" 
            style={{
              ...styles.pdfLink, 
              background: 'white', 
              color: '#667eea',
              textDecoration: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600'
            }}
            target="_blank" 
            rel="noopener noreferrer"
          >
            📄 Download Complete Business Plan PDF
          </a>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlan;